export type CheckoutIntentPlan = "premium" | "pro";
export type CheckoutIntentPeriod = "monthly" | "yearly";

export interface CheckoutIntent {
  intent: "checkout";
  plan: CheckoutIntentPlan;
  period: CheckoutIntentPeriod;
}

const STORAGE_KEY = "lingoshade_checkout_intent";

function isCheckoutIntentPlan(value: unknown): value is CheckoutIntentPlan {
  return value === "premium" || value === "pro";
}

function isCheckoutIntentPeriod(value: unknown): value is CheckoutIntentPeriod {
  return value === "monthly" || value === "yearly";
}

export function getCheckoutIntentFromSearchParams(
  searchParams: URLSearchParams,
): CheckoutIntent | null {
  const intent = searchParams.get("intent");
  const plan = searchParams.get("plan");
  const period = searchParams.get("period");

  if (
    intent !== "checkout" ||
    !isCheckoutIntentPlan(plan) ||
    !isCheckoutIntentPeriod(period)
  ) {
    return null;
  }

  return { intent: "checkout", plan, period };
}

export function saveCheckoutIntent(intent: CheckoutIntent): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
}

export function loadCheckoutIntent(): CheckoutIntent | null {
  const rawValue = sessionStorage.getItem(STORAGE_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as Partial<CheckoutIntent>;
    if (
      parsed.intent === "checkout" &&
      isCheckoutIntentPlan(parsed.plan) &&
      isCheckoutIntentPeriod(parsed.period)
    ) {
      return {
        intent: "checkout",
        plan: parsed.plan,
        period: parsed.period,
      };
    }
  } catch {
    // Ignore malformed storage values and clear them below.
  }

  sessionStorage.removeItem(STORAGE_KEY);
  return null;
}

export function clearCheckoutIntent(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function buildCheckoutIntentAuthUrl(basePath: string = "/auth"): string {
  const checkoutIntent = loadCheckoutIntent();
  if (!checkoutIntent) {
    return basePath;
  }

  return `${basePath}?intent=checkout&plan=${checkoutIntent.plan}&period=${checkoutIntent.period}`;
}

export function buildPostAuthCheckoutUrl(intent: CheckoutIntent): string {
  return `/subscription?autocheckout=1&plan=${intent.plan}&billingPeriod=${intent.period}`;
}
